import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, KeyRound, Save, User } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { profileApi, type ApiRecord } from '../../lib/api';

function Avatar({ user }: { user: ApiRecord }) {
	const url = user.avatar_url ? String(user.avatar_url) : null;
	const initials = String(user.name ?? 'U')
		.split(' ')
		.map((w) => w[0])
		.join('')
		.slice(0, 2)
		.toUpperCase();

	return url ? (
		<img
			src={url}
			alt={String(user.name ?? 'Avatar')}
			className="h-24 w-24 rounded-2xl object-cover ring-2 ring-subtle"
		/>
	) : (
		<div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-surface-hover text-2xl font-bold text-primary ring-2 ring-subtle">
			{initials}
		</div>
	);
}

export function ProfilePage() {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const { data: profile, isLoading } = useQuery({
		queryKey: ['profile'],
		queryFn: profileApi.getProfile
	});

	const [form, setForm] = useState<{
		name: string;
		title: string;
		phone: string;
		bio: string;
		password: string;
		confirmPassword: string;
	}>({
		name: '',
		title: '',
		phone: '',
		bio: '',
		password: '',
		confirmPassword: ''
	});

	const [formInitialised, setFormInitialised] = useState(false);
	if (profile && !formInitialised) {
		setForm({
			name: String(profile.name ?? ''),
			title: String(profile.title ?? ''),
			phone: String(profile.phone ?? ''),
			bio: String(profile.bio ?? ''),
			password: '',
			confirmPassword: ''
		});
		setFormInitialised(true);
	}

	const updateMutation = useMutation({
		mutationFn: async () => {
			if (form.password && form.password !== form.confirmPassword) {
				throw new Error('Passwords do not match');
			}
			const payload: Record<string, unknown> = {
				name: form.name.trim() || undefined,
				title: form.title.trim() || undefined,
				phone: form.phone.trim() || undefined,
				bio: form.bio.trim() || undefined
			};
			if (form.password) payload.password = form.password;
			return profileApi.updateProfile(payload);
		},
		onSuccess: async () => {
			toast.success('Profile saved');
			setForm((f) => ({ ...f, password: '', confirmPassword: '' }));
			await queryClient.invalidateQueries({ queryKey: ['profile'] });
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save profile')
	});

	const avatarMutation = useMutation({
		mutationFn: (file: File) => profileApi.uploadAvatar(file),
		onSuccess: async () => {
			toast.success('Avatar updated');
			await queryClient.invalidateQueries({ queryKey: ['profile'] });
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to upload avatar')
	});

	function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (file) avatarMutation.mutate(file);
		e.target.value = '';
	}

	const inputClass =
		'w-full rounded-xl border border-subtle bg-canvas px-3 py-2 text-sm text-primary placeholder:text-muted focus:border-cyan-500/50 focus:outline-none';

	if (isLoading || !profile) {
		return (
			<div className="flex h-full items-center justify-center text-muted">
				Loading profile...
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-2xl space-y-6 p-6">
			<div>
				<h1 className="text-xl font-semibold text-primary">My Profile</h1>
				<p className="mt-0.5 text-sm text-muted">Update your personal information and avatar</p>
			</div>

			{/* Avatar section */}
			<div className="flex items-center gap-5 rounded-2xl border border-subtle bg-surface-2 p-5">
				<div className="relative">
					<Avatar user={profile} />
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						disabled={avatarMutation.isPending}
						className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-xl border border-subtle bg-surface-hover text-secondary shadow transition hover:bg-surface-hover hover:text-primary"
						title="Upload avatar"
					>
						<Camera className="h-4 w-4" />
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/jpeg,image/png,image/gif"
						className="hidden"
						onChange={handleAvatarChange}
					/>
				</div>
				<div>
					<p className="font-semibold text-primary">{String(profile.name ?? '')}</p>
					{profile.title ? (
						<p className="mt-0.5 text-sm text-muted">{String(profile.title)}</p>
					) : null}
					<p className="mt-0.5 text-xs text-muted">{String(profile.email ?? '')}</p>
					<p className="mt-1.5 text-xs text-muted">
						JPEG, PNG or GIF · max 5 MB
					</p>
				</div>
			</div>

			{/* Profile form */}
			<form
				onSubmit={(e) => {
					e.preventDefault();
					updateMutation.mutate();
				}}
				className="space-y-5 rounded-2xl border border-subtle bg-surface-2 p-5"
			>
				<p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted">
					<User className="h-3.5 w-3.5" />
					Personal info
				</p>

				<div className="grid gap-4 sm:grid-cols-2">
					<label className="space-y-1.5">
						<span className="text-xs text-muted">Full name</span>
						<input
							value={form.name}
							onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
							placeholder="Your name"
							className={inputClass}
						/>
					</label>
					<label className="space-y-1.5">
						<span className="text-xs text-muted">Job title</span>
						<input
							value={form.title}
							onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
							placeholder="e.g. Sales Representative"
							className={inputClass}
						/>
					</label>
				</div>

				<label className="block space-y-1.5">
					<span className="text-xs text-muted">Phone</span>
					<input
						type="tel"
						value={form.phone}
						onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
						placeholder="+1 555 000 0000"
						className={inputClass}
					/>
				</label>

				<label className="block space-y-1.5">
					<span className="text-xs text-muted">Bio</span>
					<textarea
						value={form.bio}
						onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
						placeholder="A short bio about yourself..."
						rows={3}
						className={inputClass + ' resize-none'}
					/>
				</label>

				<div className="border-t border-subtle pt-4">
					<p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted">
						<KeyRound className="h-3.5 w-3.5" />
						Change password
					</p>
					<div className="grid gap-4 sm:grid-cols-2">
						<label className="space-y-1.5">
							<span className="text-xs text-muted">New password</span>
							<input
								type="password"
								value={form.password}
								onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
								placeholder="Leave blank to keep current"
								autoComplete="new-password"
								className={inputClass}
							/>
						</label>
						<label className="space-y-1.5">
							<span className="text-xs text-muted">Confirm password</span>
							<input
								type="password"
								value={form.confirmPassword}
								onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
								placeholder="Repeat new password"
								autoComplete="new-password"
								className={inputClass}
							/>
						</label>
					</div>
				</div>

				<div className="flex justify-end pt-1">
					<button
						type="submit"
						disabled={updateMutation.isPending}
						className="inline-flex items-center gap-2 rounded-xl bg-accent/80 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-accent disabled:opacity-50"
					>
						<Save className="h-4 w-4" />
						{updateMutation.isPending ? 'Saving…' : 'Save changes'}
					</button>
				</div>
			</form>
		</div>
	);
}
